import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { makeStyles } from "@mui/styles";
import {
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { formatPrice } from "../../utils/function";

const useStyles = makeStyles({});

export default function UnitMaterialsTable({
  title,
  items,
  hiddenColumns = [],
  loading,
  handleEdit,
  handleDelete,
}) {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [editableRowIndex, setEditableRowIndex] = useState(-1);
  const [editableRow, setEditableRow] = useState(null);
  const [isValid, setIsValid] = useState(false);

  const columns = [
    {
      name: "PROJECT_DESCRIPTION",
      label: "Project",
      options: {
        display: !hiddenColumns.includes("PROJECT_DESCRIPTION"),
      },
    },
    {
      name: "JOB_DESC",
      label: "Job",
      options: {
        display: !hiddenColumns.includes("JOB_DESC"),
      },
    },
    {
      name: "UNIT_ID",
      label: "Unit",
      options: {
        display: !hiddenColumns.includes("UNIT_ID"),
      },
    },
    {
      name: "PHASE_ID",
      label: "Phase",
      options: {
        display: !hiddenColumns.includes("PHASE_ID"),
        sort: true,
      },
    },
    {
      name: "ITEM_DESC",
      label: "Material",
    },
    {
      name: "SUPPLIER_NAME",
      label: "Supplier",
      options: {
        sort: true,
        display: !hiddenColumns.includes("SUPPLIER_NAME"),
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;

          return value ? value : "Horizon";
        },
      },
    },
    {
      name: "ITEM_COST",
      label: "Cost",
      options: {
        sort: true,
        display: !hiddenColumns.includes("ITEM_COST"),
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const val1 = parseFloat(obj1.data);
            const val2 = parseFloat(obj2.data);
            data.sort((a, b) => {
              return (
                (parseFloat(a.ITEM_COST) - parseFloat(b.ITEM_COST)) *
                (order === "asc" ? 1 : -1)
              );
            });

            return (val1 - val2) * (order === "asc" ? 1 : -1);
          };
        },
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;

          return formatPrice(data[rowIndex]?.ITEM_COST || 0);
        },
      },
    },
    {
      name: "ITEM_QTY1",
      label: "Quantity",
      options: {
        sort: true,
        display: !hiddenColumns.includes("ITEM_QTY1"),
      },
    },
    {
      name: "NEW_ITEM_QTY",
      label: "Quantity",
      options: {
        sort: true,
        display: !hiddenColumns.includes("NEW_ITEM_QTY"),
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;

          return rowIndex === editableRowIndex ? (
            <TextField
              variant="standard"
              size="small"
              type="number"
              value={editableRow.NEW_ITEM_QTY}
              onChange={handleQuantityChange}
              error={!isValid}
              helperText={
                !isValid
                  ? `This value should be between 1 and ${items[rowIndex].NEW_ITEM_QTY}.`
                  : ""
              }
            />
          ) : (
            value
          );
        },
      },
    },
    {
      name: "",
      label: "Line Total",
      options: {
        sort: false,
        display: !hiddenColumns.includes("LINE_TOTAL"),
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;

          return formatPrice(
            data[rowIndex]?.ITEM_QTY1 * data[rowIndex]?.ITEM_COST || 0
          );
        },
      },
    },
    {
      name: "ACTIONS",
      label: "ACTIONS",
      options: {
        display: Boolean(handleDelete),
        setCellProps: () => ({
          style: { minWidth: 120 },
        }),
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const rowData = data[rowIndex];

          return (
            <Grid container alignItems="center" spacing={1}>
              {handleEdit && (
                <Grid item>
                  <Grid container spacing={1}>
                    <Grid item>
                      {editableRowIndex === rowIndex ? (
                        <IconButton
                          title="save"
                          color="primary"
                          onClick={handleRowSave}
                          disabled={!isValid}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          title="edit"
                          color="primary"
                          onClick={(e) => handleRowEdit(e, rowIndex)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Grid>
                    {editableRowIndex === rowIndex && (
                      <Grid item>
                        <IconButton
                          title="cancel"
                          onClick={handleRowEditCancel}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
              {handleDelete && (
                <Grid item>
                  <IconButton
                    title="delete"
                    color="error"
                    onClick={(e) => handleRowDelete(e, rowData)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          );
        },
      },
    },
  ];

  const options = {
    filter: true,
    filterType: "dropdown",
    responsive: "standard",
    selectableRows: "none",
    pagination: false,
    rowsPerPage: data.length,
  };

  useEffect(() => {
    if (items) {
      const newItems = items.sort((a, b) => a.PHASE_ID - b.PHASE_ID);

      setData(newItems);
    }
  }, [items]);

  const handleQuantityChange = (e) => {
    const qty = parseInt(e.target.value);
    const maxQty = parseInt(items[editableRowIndex].NEW_ITEM_QTY);

    setIsValid(qty > 0 && qty <= maxQty);
    setEditableRow({
      ...editableRow,
      NEW_ITEM_QTY: qty,
    });
  };

  const handleRowEdit = (e, index) => {
    e.preventDefault();

    setEditableRowIndex(index);
    setEditableRow(data[index]);
  };

  const handleRowSave = (e) => {
    e.preventDefault();

    handleEdit(editableRow);
    setEditableRowIndex(-1);
    setEditableRow(null);
  };

  const handleRowEditCancel = () => {
    setEditableRow(null);
    setEditableRowIndex(-1);
  };

  const handleRowDelete = (e, rowData) => {
    e.preventDefault();

    handleDelete(rowData);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <MUIDataTable
          title={
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="h5">{title}</Typography>
              </Grid>
              {loading && (
                <Grid item>
                  <CircularProgress size="1rem" />
                </Grid>
              )}
            </Grid>
          }
          data={data}
          columns={columns}
          options={options}
        />
      </Grid>
    </Grid>
  );
}
